/**
 * Built-in print templates — ready to use (fill the fields, frame the
 * viewports) and the starting point for "Duplicate & customise".
 *
 * Builtins are versioned like org templates: bump `version` whenever a
 * builtin's spec changes so sheets pinned to an older version can be told
 * an update exists. Never remove a builtin id that may be pinned.
 */

import { pageSizeMm, type PaperId } from '../paper';
import type { PrintTemplateElement, PrintTemplateSpec, ViewportElement, ViewportKind } from '../spec';
import { BUILTIN_DEFAULTS, BUILTIN_FIELDS, BUILTIN_STYLES, HEAVY, PRINT_TEMPLATE_SCHEMA_VERSION, TITLE_BLOCK_SIZE, northArrow, rect, revisionTable, scaleBar, text, titleBlock, titleStrip } from './common';

/** Ready-made blocks the Template Editor inserts (ISO 7200 title block, revision table, title strip). */
const ISO_TITLE_BLOCK_SIZE = TITLE_BLOCK_SIZE;
const isoTitleBlock = titleBlock;
const isoRevisionTable = revisionTable;
const titleStripBlock = titleStrip;

export interface BuiltinPrintTemplate {
  id: string;
  version: number;
  name: string;
  description: string;
  spec: PrintTemplateSpec;
}

/** ISO 5457 frame: 20 mm filing margin on the left, 10 mm elsewhere. */
const FRAME = { top: 10, right: 10, bottom: 10, left: 20 } as const;
const VIEWPORT_INSET = 5;

function viewport(id: string, xMm: number, yMm: number, wMm: number, hMm: number, defaultKind: ViewportKind, allowedKinds: ViewportKind[], defaultScaleDen: number, label: string): ViewportElement {
  return { id, kind: 'viewport', xMm, yMm, wMm, hMm, rotationDeg: 0, label, defaultKind, allowedKinds, defaultScaleDen, frame: null };
}

/** Landscape plan sheet with frame, full-size viewport, revisions, title block, north arrow and scale bar. Repeats per floor. */
function planSheet(paper: PaperId, defaultScaleDen: number): PrintTemplateSpec {
  const { widthMm, heightMm } = pageSizeMm({ kind: 'standard', id: paper }, 'landscape');
  const innerW = widthMm - FRAME.left - FRAME.right;
  const innerH = heightMm - FRAME.top - FRAME.bottom;
  const tbX = widthMm - FRAME.right - TITLE_BLOCK_SIZE.wMm;
  const tbY = heightMm - FRAME.bottom - TITLE_BLOCK_SIZE.hMm;
  const elements: PrintTemplateElement[] = [
    viewport(
      'plan',
      FRAME.left + VIEWPORT_INSET,
      FRAME.top + VIEWPORT_INSET,
      innerW - 2 * VIEWPORT_INSET,
      innerH - 2 * VIEWPORT_INSET,
      'PLAN_VECTOR',
      ['PLAN_VECTOR', 'DENSITY_RASTER', 'HEATMAP_RASTER', 'ORTHO_RGB'],
      defaultScaleDen,
      'Plan',
    ),
    rect('frame', FRAME.left, FRAME.top, innerW, innerH, HEAVY),
    revisionTable('revisions', tbX, tbY),
    titleBlock('tb', tbX, tbY),
    northArrow('north', 'plan', widthMm - FRAME.right - VIEWPORT_INSET - 16, FRAME.top + VIEWPORT_INSET),
    scaleBar('scalebar', 'plan', FRAME.left + VIEWPORT_INSET, heightMm - FRAME.bottom - VIEWPORT_INSET - 8),
  ];
  return {
    schemaVersion: PRINT_TEMPLATE_SCHEMA_VERSION,
    defaults: BUILTIN_DEFAULTS,
    styles: BUILTIN_STYLES,
    fields: BUILTIN_FIELDS,
    pages: [{ id: 'plan', name: 'Floor plan', paper: { kind: 'standard', id: paper }, orientation: 'landscape', marginsMm: FRAME, repeat: 'perFloor', elements }],
  };
}

/** A3 landscape overview sheet for a raster render, with a compact title strip. */
function rasterSheet(kind: 'DENSITY_RASTER' | 'HEATMAP_RASTER'): PrintTemplateSpec {
  const { widthMm, heightMm } = pageSizeMm({ kind: 'standard', id: 'A3' }, 'landscape');
  const innerW = widthMm - FRAME.left - FRAME.right;
  const innerH = heightMm - FRAME.top - FRAME.bottom;
  const stripH = 16;
  const legendW = kind === 'HEATMAP_RASTER' ? 40 : 0;
  const viewportW = innerW - 2 * VIEWPORT_INSET - (legendW > 0 ? legendW + VIEWPORT_INSET : 0);
  const viewportH = innerH - stripH - 2 * VIEWPORT_INSET;
  const allowed: ViewportKind[] = kind === 'HEATMAP_RASTER' ? ['HEATMAP_RASTER', 'DENSITY_RASTER', 'ORTHO_RGB'] : ['DENSITY_RASTER', 'ORTHO_RGB', 'HEATMAP_RASTER', 'PLAN_VECTOR'];
  const elements: PrintTemplateElement[] = [
    viewport('render', FRAME.left + VIEWPORT_INSET, FRAME.top + VIEWPORT_INSET, viewportW, viewportH, kind, allowed, 100, kind === 'HEATMAP_RASTER' ? 'Heatmap' : 'Density map'),
    rect('frame', FRAME.left, FRAME.top, innerW, innerH, HEAVY),
    northArrow('north', 'render', FRAME.left + VIEWPORT_INSET + viewportW - 18, FRAME.top + VIEWPORT_INSET + 2),
    scaleBar('scalebar', 'render', FRAME.left + VIEWPORT_INSET + 2, FRAME.top + VIEWPORT_INSET + viewportH - 10),
    titleStrip('strip', FRAME.left, heightMm - FRAME.bottom - stripH, innerW),
  ];
  if (legendW > 0) {
    elements.push({
      id: 'legend',
      kind: 'legend',
      xMm: widthMm - FRAME.right - VIEWPORT_INSET - legendW,
      yMm: FRAME.top + VIEWPORT_INSET,
      wMm: legendW,
      hMm: 90,
      rotationDeg: 0,
      viewportId: 'render',
      source: 'heatmap',
      title: 'Legend',
      items: [],
      style: { styleId: 'small' },
    });
  }
  return {
    schemaVersion: PRINT_TEMPLATE_SCHEMA_VERSION,
    defaults: BUILTIN_DEFAULTS,
    styles: BUILTIN_STYLES,
    fields: BUILTIN_FIELDS,
    pages: [{ id: 'render', name: kind === 'HEATMAP_RASTER' ? 'Heatmap' : 'Density map', paper: { kind: 'standard', id: 'A3' }, orientation: 'landscape', marginsMm: FRAME, repeat: 'perFloor', elements }],
  };
}

/** A4 portrait report page: header with logo, plan, notes, footer. Repeats per floor. */
function a4Report(): PrintTemplateSpec {
  const margin = 15;
  const width = 210 - 2 * margin;
  const elements: PrintTemplateElement[] = [
    { id: 'logo', kind: 'image', xMm: margin, yMm: 12, wMm: 40, hMm: 16, rotationDeg: 0, source: { kind: 'orgLogo' }, fit: 'contain', align: 'left' },
    text('heading', margin + 45, 12, width - 45, 9, '{project.name}', { styleId: 'title' }, { align: 'right' }),
    text('subheading', margin + 45, 22, width - 45, 6, '{floor.name} — {sheet.name}', { styleId: 'value' }, { align: 'right' }),
    {
      id: 'headerRule',
      kind: 'polyline',
      xMm: margin,
      yMm: 31,
      wMm: width,
      hMm: 0,
      rotationDeg: 0,
      pointsMm: [
        [0, 0],
        [width, 0],
      ],
      closed: false,
      stroke: BUILTIN_DEFAULTS.stroke,
      fill: null,
    },
    viewport('plan', margin, 35, width, 205, 'PLAN_VECTOR', ['PLAN_VECTOR', 'DENSITY_RASTER', 'HEATMAP_RASTER', 'ORTHO_RGB'], 100, 'Plan'),
    scaleBar('scalebar', 'plan', margin, 244, 60),
    northArrow('north', 'plan', margin + width - 14, 242, 14),
    text('notes', margin, 262, width, 16, '{field.notes}', { styleId: 'small' }),
    {
      id: 'footerRule',
      kind: 'polyline',
      xMm: margin,
      yMm: 280,
      wMm: width,
      hMm: 0,
      rotationDeg: 0,
      pointsMm: [
        [0, 0],
        [width, 0],
      ],
      closed: false,
      stroke: BUILTIN_DEFAULTS.stroke,
      fill: null,
    },
    text('footerOrg', margin, 282, width / 2, 5, '{org.name} · {org.email}', { styleId: 'small' }),
    text('footerDate', margin + width / 2, 282, width / 4, 5, '{date}', { styleId: 'small' }, { align: 'center' }),
    text('footerPage', margin + (3 * width) / 4, 282, width / 4, 5, '{page.number} / {page.count}', { styleId: 'small' }, { align: 'right' }),
  ];
  return {
    schemaVersion: PRINT_TEMPLATE_SCHEMA_VERSION,
    defaults: BUILTIN_DEFAULTS,
    styles: BUILTIN_STYLES,
    fields: [...BUILTIN_FIELDS, { key: 'notes', label: 'Notes', type: 'multiline', options: [], defaultValue: '' }],
    pages: [{ id: 'report', name: 'Report page', paper: { kind: 'standard', id: 'A4' }, orientation: 'portrait', marginsMm: { top: margin, right: margin, bottom: margin, left: margin }, repeat: 'perFloor', elements }],
  };
}

export const BUILTIN_PRINT_TEMPLATES: readonly BuiltinPrintTemplate[] = [
  { id: 'a3-plan', version: 1, name: 'A3 floor plan', description: 'A3 landscape plan sheet at 1:100 with ISO title block, revisions, north arrow and scale bar. One page per floor.', spec: planSheet('A3', 100) },
  { id: 'a1-plan', version: 1, name: 'A1 floor plan', description: 'A1 landscape plan sheet at 1:50 with ISO title block, revisions, north arrow and scale bar. One page per floor.', spec: planSheet('A1', 50) },
  { id: 'a4-report', version: 1, name: 'A4 report', description: 'A4 portrait page with logo header, plan, notes and footer. One page per floor.', spec: a4Report() },
  { id: 'a3-density', version: 1, name: 'A3 density map', description: 'A3 landscape X-ray density map at 300 dpi with title strip, north arrow and scale bar.', spec: rasterSheet('DENSITY_RASTER') },
  { id: 'a3-heatmap', version: 1, name: 'A3 heatmap', description: 'A3 landscape heatmap with legend, title strip, north arrow and scale bar.', spec: rasterSheet('HEATMAP_RASTER') },
];

export function builtinPrintTemplate(id: string): BuiltinPrintTemplate | undefined {
  return BUILTIN_PRINT_TEMPLATES.find((template) => template.id === id);
}

/** An empty template to draw on: one page, the house text styles and the usual sheet fields. */
export function createBlankPrintTemplateSpec(paper: PaperId = 'A3', orientation: 'portrait' | 'landscape' = 'landscape'): PrintTemplateSpec {
  return {
    schemaVersion: PRINT_TEMPLATE_SCHEMA_VERSION,
    defaults: BUILTIN_DEFAULTS,
    styles: BUILTIN_STYLES,
    fields: BUILTIN_FIELDS,
    pages: [{ id: 'page1', name: 'Page 1', paper: { kind: 'standard', id: paper }, orientation, marginsMm: { top: 10, right: 10, bottom: 10, left: 20 }, repeat: 'none', elements: [] }],
  };
}

export { ISO_TITLE_BLOCK_SIZE, isoTitleBlock, isoRevisionTable, titleStripBlock };

/** The house text styles the ready-made blocks refer to (label, value, valueStrong, title, small). */
export const BUILTIN_TEXT_STYLES = BUILTIN_STYLES;
